// Web MIDI input for Home Key — modeled on the Synth Lab's useMidiInput but
// simplified: notes only (no CC), and it re-binds every input on hot-plug via
// access.onstatechange, so plugging a keyboard in mid-lesson just works.
//
// useHomeKeyMidi(onNoteOn(midi, velocity), onNoteOff(midi), enabled)
//   → { supported, connected, deviceNames }
//
// A note-on with velocity 0 is treated as a note-off (running-status
// convention many keyboards use).

import { useEffect, useRef, useState } from "react";

const SUPPORTED =
  typeof navigator !== "undefined" &&
  typeof navigator.requestMIDIAccess === "function";

export default function useHomeKeyMidi(onNoteOn, onNoteOff, enabled = true) {
  const onRef = useRef(onNoteOn);
  onRef.current = onNoteOn;
  const offRef = useRef(onNoteOff);
  offRef.current = onNoteOff;
  const [deviceNames, setDeviceNames] = useState([]);

  useEffect(() => {
    if (!enabled || !SUPPORTED) {
      setDeviceNames([]);
      return undefined;
    }
    let cancelled = false;
    let access = null;

    const handleMessage = (e) => {
      const data = e.data;
      if (!data || data.length < 3) return;
      const kind = data[0] & 0xf0;
      const midi = data[1];
      const velocity = data[2];
      if (kind === 0x90 && velocity > 0) onRef.current(midi, velocity);
      else if (kind === 0x80 || (kind === 0x90 && velocity === 0))
        offRef.current(midi);
    };

    // (Re)attach to every current input; called again on every statechange
    // so hot-plugged devices are picked up and unplugged ones dropped.
    const bind = () => {
      if (!access) return;
      const names = [];
      access.inputs.forEach((input) => {
        input.onmidimessage = handleMessage;
        names.push(input.name || "MIDI device");
      });
      if (!cancelled) setDeviceNames(names);
    };

    navigator
      .requestMIDIAccess({ sysex: false })
      .then((a) => {
        if (cancelled) return;
        access = a;
        bind();
        access.onstatechange = bind;
      })
      .catch(() => {
        if (!cancelled) setDeviceNames([]);
      });

    return () => {
      cancelled = true;
      if (access) {
        access.inputs.forEach((input) => {
          input.onmidimessage = null;
        });
        access.onstatechange = null;
      }
    };
  }, [enabled]);

  return { supported: SUPPORTED, connected: deviceNames.length > 0, deviceNames };
}
